import React, { useMemo } from "react";
import { Box, Typography, IconButton, Stack, alpha, useTheme } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import FormInput from "../../../../inputs/FormInput";
import useGetHotelExtrasForSelect from "../../../../../hooks/hotelExtras/useGetHotelExtrasForSelect";
import { transformOptionsToIdName } from "../../../../../utils/forms/transformHelper";

const HotelDayExtras = ({ hotelId, extras, onUpdate, isArabic }) => {
    const theme = useTheme();
    const { options: extraOptions } = useGetHotelExtrasForSelect();
    const extraSelectOptions = useMemo(() => transformOptionsToIdName(extraOptions), [extraOptions]);

    const getExtraDefaults = (extraId) => {
        const found = extraOptions.find(o => String(o.value ?? o.id) === String(extraId));
        if (!found) return { pricing_basis: 'once', sell_price: 0, cost_price: 0 };
        return {
            pricing_basis: found.default_pricing_basis || 'once',
            sell_price: parseFloat(found.default_sell_price) || 0,
            cost_price: parseFloat(found.default_cost_price) || 0,
        };
    };

    const addExtra = () => {
        const newExtra = {
            id: `ext_${Date.now()}_${Math.random()}`,
            hotel_extra_id: null,
            pricing_basis: 'once',
            quantity: 1,
            days_count: 1,
            custom_sell_price: 0,
            custom_cost_price: 0,
        };
        onUpdate([...(extras || []), newExtra]);
    };

    const removeExtra = (index) => {
        const updated = [...(extras || [])];
        updated.splice(index, 1);
        onUpdate(updated);
    };

    const updateExtraField = (index, field, value) => {
        const updated = [...(extras || [])];
        updated[index] = { ...updated[index], [field]: value };
        onUpdate(updated);
    };

    const handleExtraSelect = (index, extraId) => {
        const defaults = getExtraDefaults(extraId);
        const found = extraOptions.find(o => String(o.value ?? o.id) === String(extraId));
        const updated = [...(extras || [])];
        updated[index] = {
            ...updated[index],
            hotel_extra_id: extraId,
            name: found?.label || found?.name || updated[index]?.name || null,
            pricing_basis: defaults.pricing_basis,
            custom_sell_price: defaults.sell_price,
            custom_cost_price: defaults.cost_price,
        };
        onUpdate(updated);
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {isArabic ? '✨ الإضافات' : '✨ Extras'}
                </Typography>
                <IconButton onClick={addExtra} color="primary" size="small">
                    <AddIcon />
                </IconButton>
            </Stack>

            {(!extras || extras.length === 0) && (
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem', display: 'block', ml: 1 }}>
                    {isArabic ? 'لا توجد إضافات. اضغط + لإضافة.' : 'No extras. Click + to add.'}
                </Typography>
            )}

            {extras && extras.map((extra, index) => (
                <Box
                    key={extra.id || index}
                    sx={{
                        p: 1,
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Box sx={{ flex: 1 }}>
                            <FormInput
                                label={isArabic ? 'الإضافة' : 'Extra'}
                                type="select"
                                name={`extra_select_${hotelId}_${index}`}
                                value={extra.hotel_extra_id || ''}
                                onChange={(e) => handleExtraSelect(index, e.target.value)}
                                options={extraSelectOptions}
                                size="small"
                            />
                        </Box>
                        <IconButton size="small" color="error" onClick={() => removeExtra(index)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <FormInput
                                label={isArabic ? 'الكمية' : 'Qty'}
                                type="number"
                                name={`extra_qty_${hotelId}_${index}`}
                                value={extra.quantity}
                                onChange={(e) => updateExtraField(index, 'quantity', parseInt(e.target.value) || 1)}
                                inputProps={{ min: 1 }}
                                size="small"
                                sx={{ width: '100%' }}
                            />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <FormInput
                                label={isArabic ? 'عدد الليالي' : 'Days'}
                                type="number"
                                name={`extra_days_${hotelId}_${index}`}
                                value={extra.days_count}
                                onChange={(e) => updateExtraField(index, 'days_count', parseInt(e.target.value) || 1)}
                                inputProps={{ min: 1 }}
                                size="small"
                                sx={{ width: '100%' }}
                            />
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <FormInput
                                label={isArabic ? 'سعر البيع' : 'Sell Price'}
                                type="number"
                                name={`extra_sell_${hotelId}_${index}`}
                                value={extra.custom_sell_price}
                                onChange={(e) => updateExtraField(index, 'custom_sell_price', parseFloat(e.target.value) || 0)}
                                inputProps={{ min: 0, step: 0.01 }}
                                size="small"
                                sx={{ width: '100%' }}
                            />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <FormInput
                                label={isArabic ? 'سعر التكلفة' : 'Cost Price'}
                                type="number"
                                name={`extra_cost_${hotelId}_${index}`}
                                value={extra.custom_cost_price}
                                onChange={(e) => updateExtraField(index, 'custom_cost_price', parseFloat(e.target.value) || 0)}
                                inputProps={{ min: 0, step: 0.01 }}
                                size="small"
                                sx={{ width: '100%' }}
                            />
                        </Box>
                    </Stack>
                </Box>
            ))}
        </Box>
    );
};

export default HotelDayExtras;
